/**
 * Pannello Postazioni nella pagina negozio: griglia di card con gli oggetti
 * Magazzino assegnati a ogni postazione + assegnazione rapida via popover.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Armchair, Plus, X, Package, Search, Loader2 } from 'lucide-react';
import { stationsApi } from '../lib/api';
import { useHasPermission } from '../lib/usePermissions';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { toast } from 'sonner';

const STATUS_TONE = {
  active: 'bg-emerald-500',
  maintenance: 'bg-amber-500',
  broken: 'bg-red-500',
  dismissed: 'bg-slate-300',
};

export const StoreStationsPanel = ({ storeId }) => {
  const [data, setData] = useState({ stations: [], unassigned_items: [] });
  const [loading, setLoading] = useState(true);
  const [assignOpenFor, setAssignOpenFor] = useState(null); // station_id con popover aperto
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const canAssign = useHasPermission('inventory.assign');
  const canManage = useHasPermission('inventory.stations.manage');

  const load = useCallback(() => {
    stationsApi.list(storeId)
      .then(r => setData(r.data || { stations: [], unassigned_items: [] }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [storeId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const filteredUnassigned = useMemo(() => {
    if (!query) return data.unassigned_items;
    const q = query.toLowerCase();
    return data.unassigned_items.filter(i =>
      (i.code || '').toLowerCase().includes(q) ||
      (i.model || '').toLowerCase().includes(q) ||
      (i.category_name || '').toLowerCase().includes(q));
  }, [data.unassigned_items, query]);

  async function assign(stationId, itemId) {
    try {
      await stationsApi.assignItem(stationId, itemId);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  }

  async function unassign(stationId, itemId) {
    try {
      await stationsApi.unassignItem(stationId, itemId);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  }

  async function addStation() {
    setAdding(true);
    try {
      const r = await stationsApi.create(storeId, {});
      toast.success(`Postazione "${r.data?.name || ''}" creata`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nella creazione della postazione');
    } finally {
      setAdding(false);
    }
  }

  const addStationBtn = canManage && (
    <Button size="sm" variant="outline" onClick={addStation} disabled={adding}
            className="rounded-none h-8 text-xs" data-testid="stations-panel-add-btn">
      {adding ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
      Aggiungi postazione
    </Button>
  );

  if (loading) return <p className="text-sm text-slate-500 py-8">Caricamento postazioni…</p>;

  if (data.stations.length === 0) {
    return (
      <div className="bg-white border border-slate-200 py-16 text-center" data-testid="stations-empty">
        <Armchair className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-sm font-medium text-slate-700">Nessuna postazione configurata</p>
        {canManage ? (
          <div className="mt-4 flex justify-center">{addStationBtn}</div>
        ) : (
          <p className="text-xs text-slate-500 mt-1">Le postazioni vengono configurate dagli OPS.</p>
        )}
      </div>
    );
  }

  return (
    <div data-testid="stations-panel">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h3 className="font-heading text-lg font-bold">Postazioni</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {data.stations.length} postazioni · {data.unassigned_items.length} oggetti non assegnati
          </p>
        </div>
        {addStationBtn}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.stations.map(st => (
          <div key={st.station_id} className="bg-white border border-slate-200 flex flex-col"
               data-testid={`station-card-${st.station_id}`}>
            <div className="flex items-center gap-3 px-4 pt-3 pb-2 border-b border-slate-100">
              <span className="font-heading text-3xl font-bold text-slate-200 leading-none select-none">
                {String(st.number).padStart(2, '0')}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{st.name}</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-400">
                  {st.items.length === 0 ? 'Vuota' : `${st.items.length} oggetti`}
                </p>
              </div>
            </div>
            <div className="flex-1 px-2 py-1.5">
              {st.items.length === 0 && (
                <p className="text-xs text-slate-400 italic px-2 py-2">Nessun oggetto assegnato.</p>
              )}
              {st.items.map(i => (
                <div key={i.item_id} className="group flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50"
                     data-testid={`station-item-${i.item_id}`}>
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_TONE[i.status] || 'bg-slate-300'}`} />
                  <Link to={`/inventory/${i.item_id}`} className="font-mono text-xs text-slate-900 hover:underline shrink-0">
                    {i.code}
                  </Link>
                  <span className="text-xs text-slate-500 truncate flex-1">{i.model || i.category_name}</span>
                  {canAssign && (
                    <button type="button" onClick={() => unassign(st.station_id, i.item_id)}
                            className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-400 hover:text-red-600 transition-opacity"
                            title="Rimuovi dalla postazione"
                            data-testid={`station-unassign-${i.item_id}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {canAssign && (
              <div className="border-t border-slate-100 p-2">
                <Popover open={assignOpenFor === st.station_id}
                         onOpenChange={(v) => { setAssignOpenFor(v ? st.station_id : null); setQuery(''); }}>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm"
                            className="w-full justify-start text-xs text-slate-600 hover:text-slate-900 h-8"
                            data-testid={`station-assign-btn-${st.station_id}`}>
                      <Plus className="w-3.5 h-3.5 mr-1.5" /> Assegna oggetto
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <div className="p-2 border-b border-slate-100 relative">
                      <Search className="w-3.5 h-3.5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                      <input type="text" value={query} onChange={e => setQuery(e.target.value)}
                             placeholder="Cerca per codice, modello, categoria…"
                             className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 focus:outline-none focus:border-slate-900"
                             data-testid="station-assign-search" />
                    </div>
                    <div className="max-h-64 overflow-y-auto py-1">
                      {filteredUnassigned.length === 0 && (
                        <div className="px-3 py-4 text-xs text-slate-500 flex items-center gap-2">
                          <Package className="w-4 h-4" /> Nessun oggetto libero da assegnare.
                        </div>
                      )}
                      {filteredUnassigned.map(i => (
                        <button key={i.item_id} type="button"
                                onClick={() => { assign(st.station_id, i.item_id); setAssignOpenFor(null); }}
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50"
                                data-testid={`station-assign-option-${i.item_id}`}>
                          <code className="font-mono text-xs shrink-0">{i.code}</code>
                          <span className="text-xs text-slate-500 truncate">{i.model || i.category_name}</span>
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
